import { getAssetGroups, getInventoryServices } from '@api/asset';
import { getCollections } from '@api/collections';
import {
  getProductCategoriesList,
  getProductsByStoreId,
  getProductsByType,
  getStoreProductCategoriesWithProducts,
  getStoreProducts,
} from '@api/product';
import { getStoreReviews } from '@api/rating';
import { getStore, getStoreCharges } from '@api/store';
import { getStoreTiming } from '@api/storetiming';
import { getVisitor } from '@api/visitors';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { Container, Footer, Header, Navbar, Section } from '@components';
import {
  InventoryProductCard,
  RichCard,
  StoreTopCard,
} from '@components/cards';
import { AssetGroupCard } from '@components/cards/asset/assetgroup';
import { AssetServiceCard } from '@components/cards/asset/assetService';
import MobileControls from '@components/catalogControls/mobile-controls';
import {
  TAssetGroup,
  TCharge,
  TCollection,
  TInventory,
  TProduct,
  TProductCategory,
  TProductCategoryControl,
  TReview,
  TStore,
  TStoreTiming,
} from '@components/types';
import appConfig from '@core/appconfig';
import { checkStoreAvailablity, getSafeUrl } from '@core/utils';
import { useInView } from 'framer-motion';
import { ChevronDown, ChevronRight, Loader } from 'lucide-react';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';

interface PageProps {
  initialStore?: TStore;
  initialStoreTiming?: TStoreTiming[];
  initialReviews?: TReview[];
  initialFrequent?: TProduct[];
  initialCollections?: TCollection[];
  initialCharges?: TCharge[];
}
export const getServerSideProps: GetServerSideProps<PageProps> = async (
  context
) => {
  const { slug } = context.params;
  const storeId = parseInt(slug[0] as string);
  const [
    initialStore,
    initialStoreTiming,
    initialReviews,
    initialFrequent,
    initialCollections,
    initialCharges,
  ] = await Promise.all([
    getStore(storeId),
    getStoreTiming(storeId),
    getStoreReviews(storeId, 0, 5),
    getProductsByType(storeId, false),
    getCollections(storeId),
    getStoreCharges([storeId]),
  ]);

  return {
    props: {
      initialStore: initialStore || null,
      initialStoreTiming: initialStoreTiming || null,
      initialReviews: initialReviews || null,
      initialFrequent: initialFrequent || null,
      initialCollections: initialCollections || null,
      initialCharges: initialCharges || null,
    },
  };
};

const StoreDetail: React.FC = ({
  initialStore,
  initialStoreTiming,
  initialReviews,
  initialFrequent,
  initialCollections,
  initialCharges,
}: PageProps) => {
  const router = useRouter();
  const [store, setStore] = useState<TStore>(initialStore);
  const [searchQuery, setSearchQuery] = useState<string>(null);
  const [storeTimings, setStoreTimings] =
    useState<TStoreTiming[]>(initialStoreTiming);
  const [storeReviews, setStoreReviews] = useState<TReview[]>(initialReviews);
  const [productCategoriesList, setProductCategoriesList] = useState<
    TProductCategoryControl[]
  >([]);
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<TProductCategoryControl>(null);
  const weekDays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thrusday',
    'Friday',
    'Saturday',
  ];
  const pageSize = 10;
  const searchParams = useSearchParams();
  const [frequentlyOrdered, setFrequentlyOrdered] =
    useState<TProduct[]>(initialFrequent);
  const [frequentlyAvailed, setFrequentlyAvailed] = useState<TProduct[]>([]);
  const [products, setProducts] = useState<TProduct[]>([]);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isData, setIsData] = useState(true);
  const PAGE_LIMIT = 10;
  const [allCategories, setAllCategories] = useState<TProductCategory[]>([]);
  const [minimumCategories, setMinimumCategories] = useState<
    TProductCategory[]
  >([]);

  const [categoryId, setCategoryId] = useState<null | number>(null);
  const [storeCharges, setStoreCharges] = useState<TCharge[]>(initialCharges);
  const [expandedCategories, setExpandedCategories] = useState<number[]>(
    allCategories.map((category) => category.id)
  );
  const ref = useRef(null);
  const collapseRefs = useRef<{ [key: number]: HTMLElement }>({});
  const [services, setServices] = useState<TInventory[]>([]);
  const [assetGroups, setAssetGroups] = useState<TAssetGroup[]>([]);
  const isInView = useInView(ref);
  const [collections, setCollections] =
    useState<TCollection[]>(initialCollections);
  const [isAvailable, setIsAvailable] = useState(null);

  async function fetchVisitor() {
    const response = await getVisitor(searchParams.get('visitor_id'));
    localStorage.setItem('visitorInfo', JSON.stringify(response));
  }

  const getAllServices = async (
    categoryId: number = null,
    asset_id: number = null
  ) => {
    await getInventoryServices(store?.id, null, [asset_id])?.then((res) => {
      setServices(res);
    });
  };

  const getAllAssetGroup = async () => {
    await getAssetGroups(store?.id)?.then((res) => {
      setAssetGroups(res);
    });
  };

  const fetchProductsByCategory = (categoryId: number | null) => {
    if (store?.id > 0) {
    getProductsByStoreId(store.id, categoryId, null, 0, PAGE_LIMIT)
      .then((data) => {
        setProducts(data);
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
      });
    }
  };
  function handleCategoryChange(id: number) {
    if (categoryId === id) {
      // setCategoryId(null);
      setExpandedCategories([...expandedCategories, 0]);
    } else {
      // setCategoryId(id);
      setExpandedCategories([...expandedCategories, id]);
      fetchProductsByCategory(id);
      collapseRefs.current[id].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    setProducts([]);
    setIsData(true);
    setSkip(0);
  }

  function handleClearFilters() {
    setCategoryId(null);
    setExpandedCategories(allCategories.map((category) => category.id));
    setProducts([]);
    setIsData(true);
    setSkip(0);
  }
  function toggleAccordion(id: number) {
    if (expandedCategories.includes(id)) {
      setExpandedCategories(expandedCategories.filter((c) => c !== id));
    } else {
      setExpandedCategories([...expandedCategories, id]);
    }
  }
  async function fetchCategories(storeId: number) {
    const categories: TProductCategory[] =
      await getStoreProductCategoriesWithProducts(storeId);
    setExpandedCategories(categories.slice(0, 3).map((c) => c.id));
    setAllCategories(categories);
    setMinimumCategories(categories.slice(0, 10));
  }
  useEffect(() => {
    setIsLoading(true);
    if (store?.id > 0) {
    getProductsByStoreId(store?.id, categoryId, null, skip, PAGE_LIMIT)
      .then((data) => {
        if (skip === 0) {
          setProducts(data);
        } else {
          setProducts([...products, ...data]);
        }
        if (data.length == 0) {
          setIsData(false);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
    }
  }, [store?.id, categoryId, skip]);

  useEffect(() => {
    setSkip(0);
  }, [router?.query]);

  useEffect(() => {
    if (isInView && isData && isLoading === false) {
      setSkip((prevSkip) => prevSkip + PAGE_LIMIT);
      setIsLoading(true);
    }
  }, [isInView, isData, isLoading]);

  async function fetchFrequentlyOrdered(storeId: number) {
    const products: TProduct[] = await getProductsByType(storeId, false);
    setFrequentlyOrdered(products);
  }

  async function fetchFrequentlyAvailed(storeId: number) {
    const products: TProduct[] = await getProductsByType(storeId, true);
    setFrequentlyAvailed(products);
  }

  const updateCategoryAndProduct = (
    category_id: number,
    products: TProduct[],
    allFetched: boolean
  ) => {
    let index = productCategoriesList.findIndex((r) => r.id == category_id);
    let categoryProduct = null;
    if (index === -1) {
      console.debug('Some weird problem');
    } else {
      categoryProduct = productCategoriesList[index];
      if (categoryProduct.isLoadMore) {
        categoryProduct.products = [...categoryProduct.products, ...products];
        categoryProduct.isLoadMore = !allFetched;
        categoryProduct.pageNumber = categoryProduct.pageNumber + 1;
      }
    }
    setProductCategoriesList([...productCategoriesList]);
  };

  // Get All Products
  const getResults = (
    store_id: number,
    name: string,
    category_id: number = null,
    extend: boolean = true
  ) => {
    let index = productCategoriesList.findIndex((r) => r.id == category_id);
    let categoryProduct = null;
    if (index === -1) {
      console.debug('Some weird problem');
    } else {
      categoryProduct = productCategoriesList[index];
      setIsLoading(true);
      const res: Promise<TProduct[]> = getStoreProducts(
        category_id,
        store_id,
        name,
        categoryProduct?.pageNumber,
        pageSize
      );
      res.then((result) => {
        let allFetched = result?.length != pageSize;
        setIsLoading(false);
        updateCategoryAndProduct(category_id, result, allFetched);
      });
    }
  };

  // Get Store Timing
  const getAllStoreTiming = (store_id: number) => {
    getStoreTiming(store_id).then((result) => {
      setStoreTimings(result);
    });
  };

  useMemo(() => {
    if (searchParams.get('visitor_id')) {
      fetchVisitor();
    }
  }, [searchParams]);

  useEffect(() => {
    let id = (
      router.query.slug === undefined ? router.query.slug : router.query.slug[0]
    ) as string;
    if (!router.isReady && id == store?.id?.toString()) {
      return;
    }
    const res: Promise<TStore> = getStore(parseInt(id));
    res.then((store) => {
      setStore(store);
    });
    if (router?.query?.source && router?.query?.table_id) {
      sessionStorage.setItem(
        'tableInfo',
        JSON.stringify({
          source: router?.query?.source,
          table: router?.query?.table,
          table_id: router?.query?.table_id,
          is_ac: router?.query?.is_ac,
        })
      );
    }
  }, [router.isReady, router.query.slug]);

  useEffect(() => {
    if (store?.id > 0) {
      getAllStoreTiming(store?.id);
      getStoreReviews(store?.id, 0, 5).then((res) => {
        setStoreReviews(res);
      });
      if (selectedProductCategory?.id !== undefined) {
        getResults(store?.id, null, selectedProductCategory?.id);
      }
      getStoreCharges([store?.id]).then((result) => {
        setStoreCharges(result);
      });
    }
    getAllServices();
    getAllAssetGroup();
  }, [store]);

  // Handle Product Search Function
  const handleProductSearch = (value: string) => {
    setSearchQuery(value);
  };

  useEffect(() => {
    setProductCategoriesList(
      productCategoriesList
        .sort((a: TProductCategory, b: TProductCategory) => a.id - b.id)
        .map((r: TProductCategory) => {
          return {
            ...r,
            products: [] as TProduct[],
            isLoadMore: true,
            pageNumber: 0,
          };
        })
    );
    if (productCategoriesList?.length > 0) {
      setSelectedProductCategory({
        ...productCategoriesList[0],
        products: [],
        isLoadMore: true,
        pageNumber: 0,
      });
    }
  }, [searchQuery]);

  // Fetch products on pagenumber change
  useEffect(() => {
    if (
      selectedProductCategory?.id !== undefined &&
      selectedProductCategory?.isLoadMore == true
    ) {
      getResults(
        store.id,
        searchQuery,
        selectedProductCategory.id,
        selectedProductCategory?.pageNumber != 0
      );
    }
  }, [selectedProductCategory, store]);

  useEffect(() => {
    if (store?.id > 0) {
      fetchFrequentlyOrdered(store?.id);
      fetchFrequentlyAvailed(store?.id);
      fetchCategories(store?.id);
    }
  }, [store]);

  useEffect(() => {
    // Get overall Inventory
    if (store?.id > 0) {
      // Get products category list
      getProductCategoriesList(store?.id).then((result) => {
        setProductCategoriesList(
          result
            .sort((a: TProductCategory, b: TProductCategory) => a.id - b.id)
            .map((r: TProductCategory) => {
              return {
                ...r,
                products: [] as TProduct[],
                isLoadMore: true,
                pageNumber: 0,
              };
            })
        );
        if (result?.length > 0) {
          setSelectedProductCategory({
            ...result.sort(
              (a: TProductCategory, b: TProductCategory) => a.id - b.id
            )[0],
            products: [],
            isLoadMore: true,
            pageNumber: 0,
          });
        }
      });
      getCollections(store?.id).then((collections) => {
        setCollections(collections);
      });
    }
  }, [store]);

  const checkAvailability = async () => {
    try {
      const availability = await checkStoreAvailablity(store, storeTimings);
      setIsAvailable(availability);
    } catch (e) {
      console.error('Store availability check failed', e);
    }
  };

  useEffect(() => {
    if (store?.id > 0) {
      checkAvailability();
    }
  }, [store, storeTimings]);

  return store != undefined ? (
    <Container>
      <Head>
        <title>{`${store?.name} - ${appConfig.appName}`}</title>
        <meta name="Keywords" content={`Local Stores,${store?.name},Jhattse`} />
        <meta
          name="Description"
          content={`${store?.name} offering products and services on ${appConfig.appName}`}
        />
        <link
          rel="canonical"
          href={`https://jhattse.com/store/${store.id}/${getSafeUrl(
            store?.name
          )}`}
        />
        <meta property="og:title" content={`${store?.name} on ${appConfig.appName}`} />
        <meta
          name="og:description"
          content={`${store?.name} offering products and services on ${appConfig.appName}`}
        />
        {store?.image && <meta name="og:image" content={`${store?.image}`} />}
        <meta
          property="og:url"
          content={`https://jhattse.com/store/${store.id}/${getSafeUrl(
            store?.name
          )}`}
        />
        {store?.image != undefined && store?.image != null && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'ImageObject',
                contentUrl: store?.image,
                license: 'https://jhattse.com/terms',
                acquireLicensePage: 'https://jhattse.com/terms',
                creditText: 'Jhattse',
                creator: {
                  '@type': 'Organization',
                  name: 'Jhattse',
                },
                copyrightNotice: 'Jhattse Business',
              }),
            }}
          />
        )}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              {
                '@context': 'https://schema.org',
                '@type': 'LocalBusiness',
                logo: store.logo,
                image: [store.image],
                name: store.name,
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: store.address?.street_name || 'N/A',
                  addressLocality: store.address?.locality || 'N/A',
                  postalCode: store.address?.pincode || 'N/A',
                  addressCountry: 'IN',
                },
                review: (storeReviews || []).map((r: TReview) => {
                  return {
                    '@type': 'Review',
                    reviewRating: {
                      '@type': 'Rating',
                      ratingValue: r.rating,
                    },
                    author: {
                      '@type': 'Person',
                      name: r?.user?.full_name || 'Anonymous',
                    },
                  };
                }),
                aggregateRating: {
                  '@type': 'AggregateRating',
                  ratingValue: store.stats?.rating_overall,
                  bestRating: '5',
                  reviewCount: store.stats?.rating_count,
                },
                geo: {
                  '@type': 'GeoCoordinates',
                  latitude: store.address?.latitude,
                  longitude: store.address?.longitude,
                },
                url: `https://jhattse.com/store/${store.id}/${getSafeUrl(
                  store?.name
                )}`,
                telephone: store.phone,
                openingHoursSpecification: storeTimings
                  .filter(
                    (x) =>
                      x?.open_time != undefined && x?.close_time != undefined
                  )
                  .map((st) => {
                    return {
                      '@type': 'OpeningHoursSpecification',
                      dayOfWeek: weekDays[st?.day_of_week],
                      opens: st?.open_time,
                      closes: st?.close_time,
                    };
                  }),
                menu: `https://jhattse.com/store/${store?.id}/${getSafeUrl(
                  store?.name
                )}/menu`,
              },
              (key, value) => {
                // Check if key matches the string "pin" or "mob"
                // if matched return value "undefined"
                if (key === 'image') {
                  return store.image != undefined && store.image != null
                    ? value
                    : undefined;
                }
                if (key === 'logo') {
                  return store.logo != undefined && store.logo != null
                    ? value
                    : undefined;
                }
                if (key === 'geo') {
                  return store.address &&
                    store.address?.latitude != undefined &&
                    store.address?.longitude != undefined
                    ? value
                    : undefined;
                }
                if (key === 'aggregateRating') {
                  return store.stats?.rating_count > 0 ? value : undefined;
                }
                if (key === 'review') {
                  return storeReviews?.length > 0 ? value : undefined;
                }
                return value;
              }
            ),
          }}
        />
      </Head>
      <Header backText={store?.name} />
      <div className="max-w-[1240px] w-full font-monrope mx-auto pb-10">
        <div className="hidden py-8 px-2 md:flex items-center">
          <Breadcrumb className="text-base text-neutral-400 font-medium font-manrope list-none text-breadcrumbs">
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink href="/store">Stores</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem
              className="text-ellipsis text-left break-words line-clamp-1 select-none text-neutral-800 font-semibold"
              isCurrentPage
            >
              <BreadcrumbLink
                href={`/store/${store.id}/${getSafeUrl(store?.name || 'store')}`}
              >
                {(store?.name?.length || 0) > 80
                  ? `${store?.name.slice(0, 80)}...`
                  : store?.name || 'Store'}
              </BreadcrumbLink>
              <ol className="p-0 list-none"></ol>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>
        <div className="flex gap-4 flex-col mx-auto px-2">
          <StoreTopCard
            storeTimings={storeTimings}
            store={store}
            isAvailable={isAvailable}
          />
          {storeCharges?.length > 0 && (
            <div className="flex flex-wrap charges-strip w-full gap-2">
              {storeCharges?.map((charge) => {
                return (
                  <div className="flex gap-2 items-center justify-between px-3 py-1.5 bg-amber-50 border border-solid border-amber-200 rounded-full">
                    <span className="text-neutral-800 dark:text-neutral-200 font-semibold text-sm titlecase">
                      {charge?.charge?.display_name || charge?.charge?.name}
                    </span>
                    <span className="text-neutral-800 dark:text-neutral-200 font-semibold text-sm">
                      ₹{charge?.total_cost}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {/* Frequently Availed Services */}
          {services?.length > 0 ? (
            <div className="py-4">
              <Suspense>
                <Section
                  title="Available Services For Booking"
                  products={services}
                  element={AssetServiceCard}
                  isCarousel={true}
                  style="pb-2 text-neutral-900"
                  headStyle="md:text-lg text-base py-1 font-semibold font-manrope"
                  loading="lazy"
                />
              </Suspense>
            </div>
          ) : (
            assetGroups?.length > 0 && (
              <div className="py-4">
                <Suspense>
                  <Section
                    title="Available For Booking"
                    products={assetGroups}
                    element={AssetGroupCard}
                    isCarousel={true}
                    style="pb-2 text-neutral-900"
                    headStyle="md:text-lg text-base py-1 font-semibold font-manrope"
                    loading="lazy"
                  />
                </Suspense>
              </div>
            )
          )}
          <div className="space-y-4 lg:space-y-8 w-full">
            {frequentlyOrdered && frequentlyOrdered.length > 0 && (
              <Suspense>
                <Section
                  title="Frequently Ordered"
                  products={frequentlyOrdered}
                  element={RichCard}
                  isCarousel={true}
                  style="pb-2 text-neutral-900"
                  headStyle="md:text-lg text-base py-1 font-semibold font-manrope"
                  loading="lazy"
                  isAvailable={isAvailable}
                />
              </Suspense>
            )}

            {frequentlyAvailed &&
              !(services?.length > 0) &&
              frequentlyAvailed.length > 0 && (
                <Suspense>
                  <Section
                    title="Frequently Availed Services"
                    products={frequentlyAvailed}
                    element={RichCard}
                    isCarousel={true}
                    style="pb-2 text-neutral-900"
                    headStyle="md:text-lg text-base py-1 font-semibold font-manrope"
                    isAvailable={isAvailable}
                  />
                </Suspense>
              )}

            {collections?.length > 0 && (
              <div className="space-y-4">
                <h6 className="text-lg text-success-500 font-semibold">
                  Our Collections
                </h6>
                <div className="flex flex-row gap-4 max-h-auto overflow-x-scroll no-scrollbar pb-2">
                  {collections?.map((collection) => (
                    <div className="py-2 px-4 flex flex-col justify-between gap-2 rounded-lg bg-neutral-100 max-h-auto w-full min-w-44">
                      <div className="flex flex-col">
                        <h6 className="font-manrope text-base text-neutral-800 font-semibold capitalize">
                          {collection?.title}
                        </h6>
                        {collection?.subtitle && (
                          <span className="font-manrope text-sm text-neutral-500 font-semibold">
                            {collection?.subtitle}
                          </span>
                        )}
                      </div>
                      <Link
                        href={`/collections?store_id=${store?.id}&collection_id=${collection?.id}`}
                        prefetch={false}
                      >
                        <button
                          type="button"
                          className="px-0 flex items-center gap-0.5 cursor-pointer"
                        >
                          <span className="text-xs text-brand-500 font-manrope font-semibold uppercase">
                            Check Items
                          </span>
                          <ChevronRight className="w-4 h-4 text-brand-500" />
                        </button>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="w-full grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-3 md:gap-6 items-start">
              {/* Desktop: sticky category index */}
              <aside className="hidden md:block sticky top-4 bg-white border border-solid border-neutral-200/70 shadow-sm rounded-2xl p-3 max-h-[calc(100vh-2rem)] overflow-y-auto no-scrollbar">
                <div className="flex items-center justify-between px-2 pb-2">
                  <span className="text-sm font-bold text-neutral-900 font-manrope">Categories</span>
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="text-xs text-brand-500 font-manrope font-semibold bg-transparent border-0 cursor-pointer hover:underline"
                  >
                    Reset
                  </button>
                </div>
                <ul className="list-none m-0 p-0 flex flex-col gap-0.5">
                  {allCategories.map((category) => (
                    <li key={category.id}>
                      <button
                        type="button"
                        onClick={() => handleCategoryChange(category.id)}
                        className="w-full flex items-center justify-between gap-2 px-2 py-2 rounded-lg border-0 bg-transparent text-left cursor-pointer hover:bg-neutral-100 transition-colors"
                      >
                        <span className="text-sm font-semibold text-neutral-700 font-manrope line-clamp-1">
                          {category.name}
                        </span>
                        <span className="text-xs text-neutral-400 font-manrope flex-shrink-0">
                          {category.products?.length || 0}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </aside>

              <div className="min-w-0 flex flex-col gap-3 md:gap-4 min-h-screen">
                {/* Mobile: sticky category chips */}
                <div className="md:hidden sticky top-0 z-30 -mx-2 px-2 py-2 bg-white/95 backdrop-blur">
                  <MobileControls
                    categoryId={categoryId}
                    categories={allCategories}
                    handleCategoryChange={handleCategoryChange}
                    isScroll={true}
                  />
                </div>

                {allCategories.map((category) => {
                  const open = expandedCategories.includes(category.id);
                  return (
                    <section
                      key={category.id.toString()}
                      ref={(ref) => (collapseRefs.current[category.id] = ref)}
                      className="scroll-mt-14 md:scroll-mt-4"
                    >
                      <button
                        type="button"
                        className="w-full flex items-center justify-between gap-3 px-1 py-1.5 border-0 bg-transparent cursor-pointer font-manrope"
                        onClick={() => toggleAccordion(category.id)}
                        aria-expanded={open}
                      >
                        <span className="flex items-baseline gap-2 min-w-0">
                          <span className="text-base md:text-lg font-bold text-neutral-900 text-left line-clamp-1">
                            {category.name}
                          </span>
                          <span className="text-xs text-neutral-400 font-semibold flex-shrink-0">
                            {category.products?.length || 0} {category.products?.length === 1 ? 'item' : 'items'}
                          </span>
                        </span>
                        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-neutral-100 text-neutral-500 flex-shrink-0">
                          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </span>
                      </button>
                      {open && (
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-2.5 md:gap-3 pt-1.5">
                          {(category.products || []).map((inventory) => (
                            <InventoryProductCard
                              element={{ ...inventory, store: store }}
                              key={inventory?.id}
                              isAvailable={isAvailable}
                            />
                          ))}
                          {isLoading && (
                            <div className="px-12 py-6 flex justify-center xl:col-span-2">
                              <Loader className="w-8 h-8 text-neutral-400 animate-spin" />
                            </div>
                          )}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Navbar />
      <Footer />
    </Container>
  ) : (
    <Container>
      <div className="w-full">
        <div className="h-12 bg-neutral-200 dark:bg-neutral-700 max-w-[480px] mb-2.5"></div>
      </div>
      <div className="h-2"></div>
      <div className="space-y-8 m-5 animate-pulse md:space-y-0 md:space-x-8 md:flex md:items-center">
        <div className="flex justify-center items-center max-w-96 h-48 bg-neutral-300 rounded dark:bg-neutral-700">
          <svg
            className="w-12 h-12 text-neutral-200"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            fill="currentColor"
            viewBox="0 0 640 512"
          >
            <path d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z" />
          </svg>
        </div>
        <div className="w-full">
          <div className="h-2.5 bg-neutral-200 rounded-full dark:bg-neutral-700 w-48 mb-4 "></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[480px] mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[440px] mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[360px]"></div>
          <div className="h-2"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
          <div className="h-2 bg-neutral-200 rounded-full dark:bg-neutral-700 max-w-[460px] mb-2.5"></div>
        </div>
        <span className="sr-only">Loading...</span>
      </div>
      <Navbar />
      <Footer />
    </Container>
  );
};

export default StoreDetail;
